import * as constants from '../constants/CONSTANT';

async function parseErrorResponse(response, fallbackMessage) {
  let errorMessage = fallbackMessage;
  if (response.status === 413) {
    errorMessage = 'Upload too large. Total image size must not exceed 15 MB.';
  }
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorMessage;
  } catch (e) {
    try {
      const errorText = await response.text();
      if (errorText) errorMessage = errorText;
    } catch (_) {}
  }
  return errorMessage;
}

async function handlePdfDownloadResponse(response, objectName, options = {}) {
  const returnForDisplay = options.returnForDisplay === true;
  const defaultFilename = options.defaultFilename || `${objectName}_document.pdf`;

  const contentDisposition = response.headers.get('Content-Disposition') || '';
  let filename = defaultFilename;
  const starMatch = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (starMatch && starMatch[1]) {
    try {
      filename = decodeURIComponent(starMatch[1].trim());
    } catch (_) {}
  }
  if (filename === defaultFilename) {
    const filenameMatch = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
    if (filenameMatch && filenameMatch[1]) filename = filenameMatch[1].trim();
  }
  const safeDownloadName = filename.replace(/[^\w.-]/g, '_').replace(/_{2,}/g, '_') || 'document.pdf';
  if (!safeDownloadName.toLowerCase().endsWith('.pdf')) {
    filename = `${safeDownloadName}.pdf`;
  } else {
    filename = safeDownloadName;
  }

  const contentType = response.headers.get('Content-Type') || 'application/pdf';
  const arrayBuffer = await response.arrayBuffer();
  if (!arrayBuffer || arrayBuffer.byteLength === 0) {
    throw new Error('Download failed: received empty file. The generated PDF may be empty.');
  }
  const blob = new Blob([arrayBuffer], { type: contentType.split(';')[0].trim() });
  const blobUrl = URL.createObjectURL(blob);

  if (returnForDisplay) {
    return { blobUrl, filename };
  }

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
  return undefined;
}

function getAuthToken() {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication token not found');
  }
  return token;
}

const GenerateDocumentApi = {
  /**
   * Analyze template for upload image slots and DB field images.
   * @returns {Promise<{ uploadSlots: Array, fieldImages: Array, requiresUpload: boolean }>}
   */
  async analyzeDocumentImages(objectName, recordId, action = 'customer quote', options = {}) {
    const token = getAuthToken();
    const url = `${constants.API_BASE_URL}/api/generatedocument/analyze/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, templateId: options.templateId || null }),
    });

    if (!response.ok) {
      throw new Error(await parseErrorResponse(response, 'Failed to analyze document template'));
    }

    const payload = await response.json();
    if (!payload?.success) {
      throw new Error(payload?.message || 'Failed to analyze document template');
    }

    return payload.data || { uploadSlots: [], fieldImages: [], requiresUpload: false };
  },

  /**
   * Upload slot images via multipart (no base64 in JSON).
   * @param {Object} filesByKey - Map of slot key -> File
   * @param {string} uploadSessionId - Client session id (uuid)
   * @returns {Promise<{ uploadSessionId: string, uploadImages: Object, totalBytes: number }>}
   */
  async uploadDocumentImages(objectName, recordId, filesByKey = {}, uploadSessionId) {
    const token = getAuthToken();
    const url = `${constants.API_BASE_URL}/api/generatedocument/upload-images/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}`;

    const formData = new FormData();
    if (uploadSessionId) {
      formData.append('uploadSessionId', uploadSessionId);
    }

    Object.entries(filesByKey || {}).forEach(([slotKey, file]) => {
      if (file) {
        formData.append(`slot__${slotKey}`, file, file.name || `${slotKey}.png`);
      }
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: token,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(await parseErrorResponse(response, 'Failed to upload document images'));
    }

    const payload = await response.json();
    if (!payload?.success) {
      throw new Error(payload?.message || 'Failed to upload document images');
    }

    return payload.data || { uploadSessionId, uploadImages: {}, totalBytes: 0 };
  },

  /**
   * Generate document with upload image URLs (small JSON body after multipart upload).
   * @param {Object} uploadImages - Map of slot key -> file URL
   * @param {{ uploadSessionId?: string }} options
   */
  async generateDocumentWithUploads(
    objectName,
    recordId,
    action = 'customer quote',
    createAttachment = false,
    uploadImages = {},
    options = {}
  ) {
    try {
      const token = getAuthToken();
      const url = `${constants.API_BASE_URL}/api/generatedocument/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}`;
      const uploadSessionId = options.uploadSessionId || null;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          createAttachment,
          uploadImages,
          uploadSessionId,
          templateId: options.templateId || null,
        }),
      });

      if (!response.ok) {
        throw new Error(await parseErrorResponse(response, 'Failed to generate document'));
      }

      return handlePdfDownloadResponse(response, objectName, options);
    } catch (error) {
      console.error('Error generating document with uploads:', error);
      throw error;
    }
  },

  /**
   * Generate and download document (PDF) for an object
   * @param {string} objectName - Object name (e.g., 'quote', 'opportunity')
   * @param {string} recordId - Record ID
   * @param {string} action - Action type (e.g., 'customer quote'), defaults to 'customer quote'
   * @param {boolean} createAttachment - Whether to attach the generated document to the record
   * @param {{ returnForDisplay?: boolean, uploadImages?: Object }} options
   * @returns {Promise<void>} When options.returnForDisplay is false/undefined
   * @returns {Promise<{ blobUrl: string, filename: string }>} When options.returnForDisplay is true
   */
  async generateDocument(objectName, recordId, action = 'customer quote', createAttachment = false, options = {}) {
    const returnForDisplay = options && options.returnForDisplay === true;
    const uploadImages = options?.uploadImages;

    if (uploadImages && typeof uploadImages === 'object' && Object.keys(uploadImages).length > 0) {
      return this.generateDocumentWithUploads(
        objectName,
        recordId,
        action,
        createAttachment,
        uploadImages,
        options
      );
    }

    try {
      const token = getAuthToken();
      const templateIdParam = options?.templateId
        ? `&templateId=${encodeURIComponent(options.templateId)}`
        : '';
      const url = `${constants.API_BASE_URL}/api/generatedocument/${objectName}/${recordId}?action=${encodeURIComponent(action)}&createAttachment=${createAttachment}${templateIdParam}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error(await parseErrorResponse(response, 'Failed to generate document'));
      }

      return handlePdfDownloadResponse(response, objectName, { returnForDisplay });
    } catch (error) {
      console.error('Error generating document:', error);
      throw error;
    }
  },

  /**
   * Generate quote document from a lead: creates a quote linked to the lead and downloads the PDF.
   * Use when the user is on a lead record and wants to generate a quote (they don't need to be on a quote record).
   * @param {string} leadId - Lead record ID
   * @param {string} action - Action type (e.g. 'customer quote'), defaults to 'customer quote'
   * @param {{ returnForDisplay?: boolean }} options - If returnForDisplay is true, returns { blobUrl, filename } for in-chat view/download instead of triggering download
   * @returns {Promise<void>} When options.returnForDisplay is false/undefined
   * @returns {Promise<{ blobUrl: string, filename: string }>} When options.returnForDisplay is true
   */
  async generateQuoteFromLead(leadId, action = 'customer quote', options = {}) {
    const returnForDisplay = options && options.returnForDisplay === true;
    try {
      const token = getAuthToken();
      const url = `${constants.API_BASE_URL}/api/generatedocument/from-lead/${leadId}?action=${encodeURIComponent(action)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error(await parseErrorResponse(response, 'Failed to generate quote from lead'));
      }

      return handlePdfDownloadResponse(response, 'quote', {
        returnForDisplay,
        defaultFilename: `quote_from_lead_${leadId}.pdf`,
      });
    } catch (error) {
      console.error('Error generating quote from lead:', error);
      throw error;
    }
  },
};

export default GenerateDocumentApi;
